import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { APPLY } from "@/constants/testIds";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2 } from "lucide-react";

const COPY = {
  franchise:  { title: "Apply for a franchise", sub: "Tell us about your city, capital and timeline." },
  affiliate:  { title: "Become an affiliate", sub: "Tell us how you'd promote WeAutomate." },
  influencer: { title: "Apply as an influencer", sub: "Share your platforms and reach." },
  learn_earn: { title: "Start Learn & Earn", sub: "Tell us about your skills and availability." },
  job:        { title: "Apply to a role", sub: "Tell us which role you're applying for." },
  internship: { title: "Internship to Entrepreneurship", sub: "Tell us why ITE is for you." },
  general:    { title: "General application", sub: "Tell us how you'd like to engage with WeAutomate." },
};

const KNOWN = new Set(Object.keys(COPY));

export default function Apply() {
  const { type: raw } = useParams();
  const type = KNOWN.has(raw) ? raw : "general";
  const copy = COPY[type];
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", city: "", role_applied: "",
    extra: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/applications", {
        type, name: form.name, email: form.email, phone: form.phone,
        city: form.city, role_applied: form.role_applied,
        payload: { details: form.extra },
      });
      setDone(true);
      toast.success("Application submitted!");
    } catch { toast.error("Could not submit"); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="py-32">
        <div className="mx-auto max-w-xl px-6 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-accent flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <h1 className="font-display text-3xl mt-6">Thank you.</h1>
          <p className="mt-3 text-white/70">Your application has reached the WeAutomate team. We'll be in touch within 2 business days.</p>
          <Link to="/opportunities" className="mt-8 inline-flex items-center gap-2 text-sm text-white/60 hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Back to opportunities
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="py-20">
      <div className="mx-auto max-w-3xl px-6 lg:px-10">
        <Link to="/opportunities" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white">
          <ArrowLeft className="w-4 h-4" /> All opportunities
        </Link>
        <div data-testid={APPLY.type(type)} className="mt-6">
          <div className="text-[11px] uppercase tracking-widest text-white/40 font-mono">{type}</div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tighter mt-3">{copy.title}</h1>
          <p className="mt-3 text-white/60">{copy.sub}</p>
        </div>
        <form onSubmit={submit} className="mt-10 glass rounded-2xl p-7 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <input required data-testid={APPLY.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="Full name" className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD]" />
            <input required data-testid={APPLY.email} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                   placeholder="Email" className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD]" />
            <input data-testid={APPLY.phone} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                   placeholder="Phone" className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD]" />
            <input data-testid={APPLY.city} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                   placeholder="City" className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD]" />
          </div>
          {(type === "job" || type === "internship") && (
            <input data-testid={APPLY.roleApplied} value={form.role_applied} onChange={(e) => setForm({ ...form, role_applied: e.target.value })}
                   placeholder="Role you're applying for"
                   className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD]" />
          )}
          <textarea data-testid={APPLY.payload} value={form.extra} onChange={(e) => setForm({ ...form, extra: e.target.value })}
                    placeholder="Tell us a bit more — context, links, anything we should know"
                    rows={5} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD]" />
          <button data-testid={APPLY.submit} disabled={busy}
                  className="px-6 py-3 rounded-full gradient-bg font-medium disabled:opacity-50">
            {busy ? "Submitting…" : "Submit application"}
          </button>
        </form>
      </div>
    </div>
  );
}
